import React, {
  DragEvent,
  PointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useGetClientPortalQuery } from "../../../slices/ClientPortalSlice";
import {
  fetchFirmPdf,
  useCreateSignatureMutation,
} from "../../../slices/SignaturesSlice";
import { personName } from "../../clientPortal/clientPortal.utils";
import { errorText, modalInput } from "../../clientPortal/PortalModal";
import { PdfPages } from "../components/PdfPages";
import {
  SendSettings,
  SendSignatureModal,
} from "../components/SendSignatureModal";
import { DraftField, DraftSigner, FieldType } from "../signature.types";
import {
  buildSignaturePayload,
  draftProblem,
  FIELD_LABELS,
  MAX_SIGNERS,
  moveField,
  placeField,
  SIGNER_COLORS,
} from "../signature.utils";

const FIELD_TYPES: FieldType[] = [
  "signature",
  "initials",
  "date",
  "name",
  "text",
  "checkbox",
];
const FIELD_ICONS: Record<FieldType, string> = {
  signature: "✍",
  initials: "AB",
  date: "📅",
  name: "Aa",
  text: "T",
  checkbox: "☑",
};

let nextKey = 1;
const newKey = () => `k${nextKey++}`;

const hexA = (hex: string, alpha: number) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

/** Firm: choose signers and place their fields on the PDF (step 1), then send (step 2). */
export const PrepareSignaturePage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const customerId = params.get("customer") ?? "";
  const documentId = params.get("doc") ?? "";
  const portal = useGetClientPortalQuery({ customerId }, { skip: !customerId });
  const [create, createState] = useCreateSignatureMutation();

  const [signers, setSigners] = useState<DraftSigner[]>([]);
  const [fields, setFields] = useState<DraftField[]>([]);
  const [active, setActive] = useState<string>("");
  const [tool, setTool] = useState<FieldType | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const documentName = params.get("name") ?? "";
  const drag = useRef<{
    key: string;
    startX: number;
    startY: number;
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);

  const people = useMemo(
    () =>
      (portal.data?.people ?? []).filter(
        (p) => p.status !== "revoked" && p.email,
      ),
    [portal.data],
  );

  // Start with the client's first contact person.
  useEffect(() => {
    if (signers.length || !people.length) return;
    const p = people[0];
    const first: DraftSigner = {
      key: newKey(),
      kind: "client",
      name: personName(p),
      email: p.email,
      color: SIGNER_COLORS[0],
    };
    setSigners([first]);
    setActive(first.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people]);

  const recolor = (list: DraftSigner[]) =>
    list.map((s, i) => ({
      ...s,
      color: SIGNER_COLORS[i % SIGNER_COLORS.length],
    }));

  const addSigner = (s: Omit<DraftSigner, "key" | "color">) => {
    if (signers.length >= MAX_SIGNERS)
      return toast(`At most ${MAX_SIGNERS} signers.`, { type: "error" });
    const signer = { ...s, key: newKey(), color: "" };
    setSigners((list) => recolor([...list, signer]));
    setActive(signer.key);
  };

  const removeSigner = (key: string) => {
    setSigners((list) => recolor(list.filter((s) => s.key !== key)));
    setFields((list) => list.filter((f) => f.signerKey !== key));
    if (active === key)
      setActive(signers.find((s) => s.key !== key)?.key ?? "");
  };

  const moveSigner = (key: string, by: number) =>
    setSigners((list) => {
      const i = list.findIndex((s) => s.key === key);
      const j = i + by;
      if (i < 0 || j < 0 || j >= list.length) return list;
      const copy = [...list];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return recolor(copy);
    });

  const place = (type: FieldType, page: number, x: number, y: number) => {
    if (!active) return toast("Add a signer first.", { type: "error" });
    setFields((list) => [
      ...list,
      placeField(type, active, page, x, y, newKey()),
    ]);
    setProblem(null);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    const type = e.dataTransfer.getData("text/field-type") as FieldType;
    const pageEl = (e.target as HTMLElement).closest(
      "[data-page]",
    ) as HTMLElement | null;
    if (!type || !pageEl) return;
    e.preventDefault();
    const rect = pageEl.getBoundingClientRect();
    place(
      type,
      Number(pageEl.dataset.page),
      (e.clientX - rect.left) / rect.width,
      (e.clientY - rect.top) / rect.height,
    );
  };

  const startMove = (e: PointerEvent<HTMLDivElement>, f: DraftField) => {
    if ((e.target as HTMLElement).closest("button")) return;
    const page = e.currentTarget.parentElement!.getBoundingClientRect();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      key: f.key,
      startX: e.clientX,
      startY: e.clientY,
      x: f.x,
      y: f.y,
      w: page.width,
      h: page.height,
    };
    setActive(f.signerKey);
  };
  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const x = d.x + (e.clientX - d.startX) / d.w;
    const y = d.y + (e.clientY - d.startY) / d.h;
    setFields((list) =>
      list.map((f) => (f.key === d.key ? moveField(f, x, y) : f)),
    );
  };
  const endMove = () => (drag.current = null);

  const next = () => {
    const p = draftProblem(signers, fields);
    setProblem(p);
    if (!p) setSendOpen(true);
  };

  const send = async (settings: SendSettings) => {
    const payload = buildSignaturePayload(
      { customerId, documentId, ...settings },
      signers,
      fields,
    );
    const result = await create(payload);
    if ("error" in result) return errorText(result.error);
    toast("Sent for signature", { type: "success" });
    navigate(-1);
    return null;
  };

  const loadPdf = () => fetchFirmPdf(`api/signatures/document/${documentId}`);

  if (!customerId || !documentId)
    return (
      <div className={"p-10 text-sm font-semibold text-[#6B7185]"}>
        Choose a PDF from the client’s Signatures card.
      </div>
    );

  const signerOf = (key: string) => signers.find((s) => s.key === key);
  const hasFirm = signers.some((s) => s.kind === "firm");
  const available = people.filter(
    (p) =>
      !signers.some((s) => s.email.toLowerCase() === p.email.toLowerCase()),
  );
  const clientName = portal.data?.clientName ?? "";

  return (
    <div
      className={
        "flex min-h-full flex-col bg-[#F1F2F5] font-Manrope text-[#1F2433]"
      }>
      <div
        className={
          "sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b border-[#E8E8EC] bg-white px-5 py-3"
        }>
        <button
          type={"button"}
          onClick={() => navigate(-1)}
          className={
            "h-9 rounded-[9px] border-[1.5px] border-[#DCE2EA] px-3 text-[13px] font-bold text-[#3A4256]"
          }>
          ← Back
        </button>
        <div className={"flex min-w-0 flex-1 flex-col"}>
          <span className={"truncate text-[15px] font-extrabold"}>
            Prepare for signature{clientName ? ` · ${clientName}` : ""}
          </span>
          <span className={"text-[12px] font-semibold text-[#6B7185]"}>
            Step 1 of 2 · Place fields
          </span>
        </div>
        <button
          type={"button"}
          onClick={next}
          className={
            "h-10 rounded-[9px] bg-[#6B4EFF] px-5 text-[13.5px] font-extrabold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)]"
          }>
          Next
        </button>
      </div>
      {problem && (
        <div
          className={
            "border-b border-[#F3C5C5] bg-[#FDF1F1] px-5 py-2 text-center text-[13px] font-semibold text-[#B42318]"
          }>
          {problem}
        </div>
      )}

      <div className={"flex min-h-0 flex-1 gap-5 px-5 py-5"}>
        <aside
          className={
            "sticky top-[84px] flex h-fit w-[280px] shrink-0 flex-col gap-4"
          }>
          <section
            className={
              "flex flex-col gap-2.5 rounded-[14px] border border-[#E8E8EC] bg-white p-4"
            }>
            <h3
              className={
                "m-0 text-[13px] font-extrabold uppercase tracking-[0.4px] text-[#6B7185]"
              }>
              Signers
            </h3>
            {signers.map((s, i) => (
              <div
                key={s.key}
                role={"button"}
                tabIndex={0}
                onClick={() => setActive(s.key)}
                onKeyDown={(e) => e.key === "Enter" && setActive(s.key)}
                className={`flex cursor-pointer items-center gap-2 rounded-[10px] border-[1.5px] px-2.5 py-2 ${
                  active === s.key ? "" : "border-[#EDEFF3]"
                }`}
                style={
                  active === s.key
                    ? { borderColor: s.color, background: hexA(s.color, 0.08) }
                    : undefined
                }>
                <span
                  className={
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold text-white"
                  }
                  style={{ background: s.color }}>
                  {i + 1}
                </span>
                <span className={"flex min-w-0 flex-1 flex-col"}>
                  <span className={"truncate text-[13px] font-bold"}>
                    {s.kind === "firm"
                      ? "You (countersign)"
                      : s.name || s.email}
                  </span>
                  <span
                    className={
                      "truncate text-[11.5px] font-semibold text-[#6B7185]"
                    }>
                    {s.kind === "firm" ? "Signs in ProCharted" : s.email} ·{" "}
                    {fields.filter((f) => f.signerKey === s.key).length} fields
                  </span>
                </span>
                <span className={"flex flex-col"}>
                  <button
                    type={"button"}
                    aria-label={"Move up"}
                    className={"text-[10px] text-[#9097A8]"}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveSigner(s.key, -1);
                    }}>
                    ▲
                  </button>
                  <button
                    type={"button"}
                    aria-label={"Move down"}
                    className={"text-[10px] text-[#9097A8]"}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveSigner(s.key, 1);
                    }}>
                    ▼
                  </button>
                </span>
                <button
                  type={"button"}
                  aria-label={"Remove signer"}
                  className={"px-1 text-[#9097A8] hover:text-[#B42318]"}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSigner(s.key);
                  }}>
                  ✕
                </button>
              </div>
            ))}
            {adding ? (
              <div
                className={
                  "flex flex-col gap-2 rounded-[10px] bg-[#F7F8FA] p-2.5"
                }>
                {available.map((p) => (
                  <button
                    key={p.email}
                    type={"button"}
                    className={
                      "rounded-[8px] bg-white px-2.5 py-2 text-left text-[12.5px] font-bold hover:bg-[#F4F2FE]"
                    }
                    onClick={() => {
                      addSigner({
                        kind: "client",
                        name: personName(p),
                        email: p.email,
                      });
                      setAdding(false);
                    }}>
                    {personName(p)}{" "}
                    <span className={"font-semibold text-[#6B7185]"}>
                      · {p.email}
                    </span>
                  </button>
                ))}
                <input
                  className={modalInput}
                  placeholder={"Name"}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <input
                  className={modalInput}
                  placeholder={"Email"}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
                <div className={"flex gap-2"}>
                  <button
                    type={"button"}
                    className={
                      "flex-1 rounded-[8px] border border-[#DCE2EA] bg-white py-1.5 text-[12.5px] font-bold"
                    }
                    onClick={() => setAdding(false)}>
                    Cancel
                  </button>
                  <button
                    type={"button"}
                    className={
                      "flex-1 rounded-[8px] bg-[#6B4EFF] py-1.5 text-[12.5px] font-bold text-white disabled:opacity-50"
                    }
                    disabled={!newEmail.trim()}
                    onClick={() => {
                      addSigner({
                        kind: "client",
                        name: newName.trim(),
                        email: newEmail.trim(),
                      });
                      setNewName("");
                      setNewEmail("");
                      setAdding(false);
                    }}>
                    Add
                  </button>
                </div>
                <span className={"text-[11.5px] font-medium text-[#6B7185]"}>
                  New people get access to {clientName || "this client"}’s
                  portal when they confirm their email code.
                </span>
              </div>
            ) : (
              <div className={"flex flex-wrap gap-2"}>
                <button
                  type={"button"}
                  className={"text-[13px] font-bold text-[#6B4EFF]"}
                  onClick={() => setAdding(true)}>
                  + Add client signer
                </button>
                {!hasFirm && (
                  <button
                    type={"button"}
                    className={"text-[13px] font-bold text-[#6B4EFF]"}
                    onClick={() =>
                      addSigner({ kind: "firm", name: "You", email: "" })
                    }>
                    + Add me
                  </button>
                )}
              </div>
            )}
          </section>

          <section
            className={
              "flex flex-col gap-2 rounded-[14px] border border-[#E8E8EC] bg-white p-4"
            }>
            <h3
              className={
                "m-0 text-[13px] font-extrabold uppercase tracking-[0.4px] text-[#6B7185]"
              }>
              Fields
            </h3>
            <span className={"text-[12px] font-medium text-[#6B7185]"}>
              For{" "}
              <strong style={{ color: signerOf(active)?.color }}>
                {signerOf(active)?.kind === "firm"
                  ? "you"
                  : signerOf(active)?.name || signerOf(active)?.email || "—"}
              </strong>
              : drag onto the page, or click one and then click the page.
            </span>
            <div className={"grid grid-cols-2 gap-2"}>
              {FIELD_TYPES.map((t) => (
                <button
                  key={t}
                  type={"button"}
                  draggable={true}
                  onDragStart={(e) =>
                    e.dataTransfer.setData("text/field-type", t)
                  }
                  onClick={() => setTool(tool === t ? null : t)}
                  className={`flex items-center gap-2 rounded-[9px] border-[1.5px] px-2.5 py-2 text-left text-[12.5px] font-bold ${
                    tool === t
                      ? "border-[#6B4EFF] bg-[#F4F2FE] text-[#4B2FC7]"
                      : "border-[#EDEFF3] text-[#3A4256]"
                  }`}>
                  <span className={"w-5 text-center text-[12px]"}>
                    {FIELD_ICONS[t]}
                  </span>
                  {FIELD_LABELS[t]}
                </button>
              ))}
            </div>
          </section>
        </aside>

        <div
          className={`min-w-0 flex-1 ${tool ? "cursor-crosshair" : ""}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}>
          {documentName && (
            <div
              className={
                "mb-3 text-center text-[13px] font-bold text-[#6B7185]"
              }>
              {documentName}
            </div>
          )}
          <PdfPages
            load={loadPdf}
            loadKey={documentId}
            maxWidth={820}
            onPageClick={(page, x, y) => {
              if (!tool) return;
              place(tool, page, x, y);
              setTool(null);
            }}
            overlay={(page) =>
              fields
                .filter((f) => f.page === page)
                .map((f) => {
                  const s = signerOf(f.signerKey);
                  const color = s?.color ?? "#6B4EFF";
                  return (
                    <div
                      key={f.key}
                      onPointerDown={(e) => startMove(e, f)}
                      onPointerMove={onMove}
                      onPointerUp={endMove}
                      className={
                        "group absolute flex cursor-move touch-none items-center justify-center rounded-[4px] border-[1.5px] text-[11px] font-extrabold"
                      }
                      style={{
                        left: `${f.x * 100}%`,
                        top: `${f.y * 100}%`,
                        width: `${f.width * 100}%`,
                        height: `${f.height * 100}%`,
                        borderColor: color,
                        background: hexA(color, 0.16),
                        color,
                      }}
                      title={`${FIELD_LABELS[f.type]} · ${
                        s?.kind === "firm" ? "You" : s?.name || s?.email
                      }`}>
                      <span className={"truncate px-1"}>
                        {f.type === "checkbox" ? "☐" : FIELD_LABELS[f.type]}
                      </span>
                      <button
                        type={"button"}
                        aria-label={"Remove field"}
                        onClick={() =>
                          setFields((list) =>
                            list.filter((x) => x.key !== f.key),
                          )
                        }
                        className={
                          "absolute -right-2 -top-2 hidden h-5 w-5 items-center justify-center rounded-full bg-[#1F2433] text-[10px] text-white group-hover:flex"
                        }>
                        ✕
                      </button>
                    </div>
                  );
                })
            }
          />
        </div>
      </div>

      {sendOpen && (
        <SendSignatureModal
          signers={signers}
          clientName={clientName}
          documentName={documentName}
          busy={createState.isLoading}
          onClose={() => setSendOpen(false)}
          onSend={send}
        />
      )}
    </div>
  );
};
